import { Button, Icon } from '@innovationnorway/cl-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OneWayInOpportunityStatusDto, OneWayInStatus, SivaPhase } from 'src/api/v2';
import Table from 'src/components/Table';
import ReadMore from 'src/components/ReadMore/ReadMore';
import { formatDateToNOString } from 'src/utils/FormatValue';

import './HistoryTableSection.scss';
import { EmailViewContent } from './OneWayInHistoryV2';
import { useUsers } from 'src/features/users';
import { useParams } from 'react-router-dom';
import { PageParams } from '../../types/PageParams';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import { useClusters } from 'src/features/cluster';

export type HistoryTableSectionProps = {
  date: Date;
  data: OneWayInOpportunityStatusDto[];
  openEmailView?: (content: EmailViewContent) => void;
};

const HistoryTableSection: React.FC<HistoryTableSectionProps> = ({ date, data, openEmailView }) => {
  const { t: tCommon } = useTranslation();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tSiva } = useTranslation('siva');
  const { users } = useUsers();
  const { formId } = useParams<PageParams>();
  const { oneWayInGroupedFormWOEnrollmentsQuery } = useOneWayIn(formId);
  const { clusters } = useClusters();

  function statusToIcon(item: OneWayInOpportunityStatusDto): React.ReactNode {
    switch (item.status) {
      case OneWayInStatus.ReOpened:
        return <Icon prefix="fasr" iconName="folder-open" />;
      case OneWayInStatus.Opened:
        return <Icon prefix="fasr" iconName="envelope-open" />;
      case OneWayInStatus.Closed:
        return <Icon prefix="fasr" iconName="box-archive" />;
      case OneWayInStatus.AutoArchived:
        return <Icon prefix="fasr" iconName="box-archive" />;
      case OneWayInStatus.Forwarded:
        return <Icon prefix="fasr" iconName="paper-plane" />;
      case OneWayInStatus.Shared: {
        if (item.shareOpportunityCustomerEmail) return <Icon prefix="fasr" iconName="envelope" />;
        return <Icon prefix="fasr" iconName="paper-plane" />;
      }
      case OneWayInStatus.Active: {
        // Tatt opp til idé
        if (item.sivaPhase == SivaPhase.Idea) {
          return <Icon prefix="fasr" iconName="lightbulb" />;
        }

        // Anbefalt tjeneste
        if (item.recommendedServices.length > 0) {
          return <Icon prefix="fasr" iconName="hand-holding-heart" />;
        }

        // Registrert tjeneste
        if (item.registeredServices.length > 0) {
          return <Icon prefix="fasr" iconName="hands-holding-heart" />;
        }

        // Tatt opp i Siva-program
        if (item.admittedToProgram || isActivePhase(item.sivaPhase)) {
          return <Icon prefix="fasr" iconName="handshake" />;
        }

        // Notat
        if (item.description !== '') {
          return <Icon prefix="fasr" iconName="sticky-note" />;
        }

        return null;
      }
      case OneWayInStatus.Received:
        return <Icon prefix="fasr" iconName="down-to-bracket" />;
      case OneWayInStatus.FollowUpLater:
        return <Icon prefix="fasr" iconName="clock" />;
      case OneWayInStatus.EmailReceivedFromCustomer:
        return <Icon prefix="fasr" iconName="messages" />;
      case OneWayInStatus.EmailSentToCustomer:
        return <Icon prefix="fasr" iconName="messages" />;
      case OneWayInStatus.ReferredLater:
        return <Icon prefix="fasr" iconName="paper-plane" />;

      default:
        return null;
    }
  }

  function statusToText(item: OneWayInOpportunityStatusDto): string {
    switch (item.status) {
      case OneWayInStatus.Active: {
        if (item.sivaPhase != null) {
          if (item.sivaPhase == SivaPhase.Idea) {
            return tSiva('ideaConsidered');
          } else {
            return tSiva('admittedToProgram');
          }
        }

        if (item.recommendedServices.length > 0) {
          return tOneWayIn('services.recommended');
        }

        if (item.registeredServices.length > 0) {
          return tOneWayIn('services.delivered');
        }

        if (item.admittedToProgram) {
          return tSiva('admittedToProgram');
        }

        if (item.description !== '') {
          return tCommon('note');
        }

        return tOneWayIn(`status.${item.status}` as const);
      }
      case OneWayInStatus.Forwarded:
      case OneWayInStatus.Shared: {
        if (item.shareOpportunityCustomerEmail) return tCommon('email');
        return tOneWayIn('status.Referred');
      }
      case OneWayInStatus.Closed:
        return tOneWayIn('status.Archived');
      case OneWayInStatus.FollowUpLater:
        return tOneWayIn('status.FollowUpLater');
      case OneWayInStatus.EmailReceivedFromCustomer:
        return tOneWayIn('status.EmailReceivedFromCustomer');
      case OneWayInStatus.EmailSentToCustomer:
        return tOneWayIn('status.EmailSentToCustomer');
      default:
        return tOneWayIn(`status.${item.status}` as const);
    }
  }

  function renderStatusExtraInfo(item: OneWayInOpportunityStatusDto): React.ReactNode {
    switch (item.status) {
      case OneWayInStatus.Shared:
        return item.shareOpportunityCustomerEmail == null ? (
          <small>
            {tCommon('to')} {item.referenceClusterName}
          </small>
        ) : (
          <small>{tOneWayIn('history.sentToCustomer')}</small>
        );
      case OneWayInStatus.Received:
        return (
          <small>
            {tCommon('from')} {item.referenceClusterName}
          </small>
        );
      case OneWayInStatus.Active: {
        if (item.recommendedServices.length > 0) {
          const clusterName = clusters.find(
            (cluster) => cluster.id === item.recommendedServices[0].clusterId,
          )?.name;
          return (
            <>
              <ul className="margin-y--0">
                {item.recommendedServices.map((s) => (
                  <li key={s.id}>
                    <small>{s.name}</small>
                  </li>
                ))}
              </ul>
              <small>{clusterName && `${tCommon('at')} ${clusterName}`}</small>
            </>
          );
        }

        if (item.registeredServices.length > 0) {
          return (
            <ul className="margin-y--0">
              {item.registeredServices.map((s) => (
                <li key={s.id}>
                  <small>{s.name}</small>
                </li>
              ))}
            </ul>
          );
        }

        return null;
      }
      case OneWayInStatus.EmailSentToCustomer:
        return <span>{tOneWayIn('history.sentToCustomerWithoutReply')}</span>;
      case OneWayInStatus.EmailReceivedFromCustomer:
        return <span>{tOneWayIn('history.sentToCustomerWithReply')}</span>;
      case OneWayInStatus.ReferredLater:
        return (
          <small>
            {tCommon('to')} {item.clusterName}
          </small>
        );
      default:
        return null;
    }
  }

  function renderLastCell(item: OneWayInOpportunityStatusDto): React.ReactNode {
    // If a service is recommended during referral, no recommendation email is sent, so there is no content to show
    const recommendedDuringReferral =
      item.recommendationEmail?.emailContent === '' && item.recommendedServices.length > 0;
    if (recommendedDuringReferral) {
      return null;
    }

    // E-mails produced by recommending own services or referring to another actor should be displayed
    // E-mails to customers should not be shown in the acitvity log, only who sent it
    const hasEmail =
      !!item.recommendationEmail?.emailContent || !!item.shareOpportunityActorEmail?.emailContent;
    if (hasEmail) {
      return (
        <Button
          type="button"
          variant="tertiary"
          onClick={() => !!openEmailView && openEmailView(emailContent(item))}
        >
          <Icon prefix="fasr" iconName="browsers" />
          {tOneWayIn('history.readEmail')}
        </Button>
      );
    }

    return <ReadMore buttonBeside>{item.description || ''}</ReadMore>;
  }

  const dateString = useMemo(() => {
    return formatDateToNOString(new Date(date));
  }, [date]);

  const emailContent = (item: OneWayInOpportunityStatusDto) => {
    if (item.shareOpportunityActorEmail) {
      return {
        title: 'Ny henvisning i Flerbedriftsportalen',
        from: 'noreply@innovasjonnorge.no',
        to: item.shareOpportunityActorEmail?.recipients?.join(', ') || '',
        date: formatDateToNOString(item.createdOn, false, '{date}.{month}.{year}', true),
        text: item.shareOpportunityActorEmail?.emailContent || '',
        recommendedMail: false,
        referralMailActor: true,
        referralMailCustomer: false,
        opportunityTitle: oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel || '',
        receivingActor: item.referenceClusterName,
        fromActorName: item.clusterName,
        handlerName: item.handlersName,
      };
    } else {
      return {
        title: `Anbefalte tjenester hos ${item.recommendationEmail?.senderOrganisation}`,
        from: 'noreply@innovasjonnorge.no',
        to: item.recommendationEmail?.recipientName || '',
        date: formatDateToNOString(item.createdOn, false, '{date}.{month}.{year}', true),
        text: item.recommendationEmail?.emailContent || '',
        recommendedMail: true,
        referralMailActor: false,
        referralMailCustomer: false,
        actorName: item.recommendationEmail?.senderOrganisation,
        recommendedServices: item.recommendedServices.map((service) => ({
          id: service.id || '',
          name: service.name,
          owner: service.businessName,
          links: service.externalLinks,
          description: service.description,
        })),
        handlerName: item.recommendationEmail?.senderName,
        handlerEmail: users.find((u) => u.id === item.clusterUserId)?.emailAddress,
        handlerPhone: users.find((u) => u.id === item.clusterUserId)?.phone,
      };
    }
  };

  const isActivePhase = (phase: SivaPhase | undefined) =>
    phase != null && phase != SivaPhase.Idea && phase != SivaPhase.Alumni;

  const sortedData = data.sort((a, b) => (a.createdOn > b.createdOn ? -1 : 1));

  const activePhases = sortedData.filter((value) => isActivePhase(value.sivaPhase));

  return (
    <>
      <Table.Row className="owi-history__table-header--no-bottom-border">
        <Table.Cell colSpan={5}>
          <strong>{dateString}</strong>
        </Table.Cell>
      </Table.Row>

      {sortedData
        .filter((value) => {
          if (activePhases.length > 1 && isActivePhase(value.sivaPhase) && value != activePhases[0])
            return false;
          return true;
        })
        .map((item) => {
          return (
            <React.Fragment key={item.id}>
              <Table.Row className="one-way-in-history__row-content">
                {/* Icons representing the status of the item */}
                <Table.Cell data-level="1">{statusToIcon(item)}</Table.Cell>
                {/* Activity / Aktivitet */}
                <Table.Cell data-level="1">
                  <div className="display--flex flex-direction--column">
                    <strong>{statusToText(item)}</strong>
                    {renderStatusExtraInfo(item)}
                  </div>
                </Table.Cell>
                {/* Actor / Aktør */}
                <Table.Cell data-level="1">
                  {item.status === OneWayInStatus.ReferredLater
                    ? tCommon('innovationNorway')
                    : item.clusterName}
                </Table.Cell>
                {/* Handler / Behandler */}
                <Table.Cell data-level="1">
                  {(item.status !== OneWayInStatus.Opened && item.handlersName) || ''}
                </Table.Cell>
                {/* Description / Beskrivelse */}
                <Table.Cell data-level="1">{renderLastCell(item)}</Table.Cell>
              </Table.Row>
            </React.Fragment>
          );
        })}
    </>
  );
};

HistoryTableSection.displayName = 'Tr';

export default HistoryTableSection;
